import {User} from "../../api/accessControl/User.ts";
import {useSearchParams} from "react-router-dom";
import LoadingFrame from "../../components/LoadingFrame.tsx";
import {useEffect, useState} from "react";
import {BackendApi} from "../../api/api.ts";
import {Header} from "../../components/general/Header.tsx";
import {SecondaryButton} from "../../components/general/SecondaryButton.tsx";
import {resolvePaymentProviderDisplayName} from "../../lib/institutionDisplayNames.ts";
import {PaymentProviderConnectSuccessPage} from "./PaymentProviderConnectSuccessPage.tsx";
import {AccountingConnectSuccessPage} from "./AccountingConnectSuccessPage.tsx";

function ErrorView(props: { error: string, onClick: () => void }) {
    return <div className="mx-auto max-w-2xl text-center">
        <div>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                 stroke-width="1.5" stroke="currentColor"
                 className="size-64 text-primary opacity-70 mx-auto">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"/>
            </svg>
        </div>
        <div className="p-2 rounded-md">
            {props.error}
        </div>
        <SecondaryButton title={"Terug naar instellingen"} onClick={props.onClick
        } className="mx-auto mt-8"/>
    </div>;
}

function LoadingView() {
    return <div className="flex items-center flex-col my-8">
        <div role="status">
            <svg aria-hidden="true"
                 className="w-32 h-32 text-gray-200 animate-spin dark:text-gray-600 fill-primary"
                 viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"/>
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"/>
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    </div>;
}

function resolveParamError(ref: string | null, connectionId: string | null): string {
    if (!ref) {
        return 'Something went wrong. Please contact us via hello@pocketcfo.io | CODE: ERR53489';
    }
    if (!connectionId) {
        return 'Je hebt de configuratie niet afgerond.';
    }
    return '';
}

export function FinancialServiceIntegrationSuccessPage({currentUser}: { currentUser: User }) {
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');
    const connectionId = searchParams.get('connectionId');
    const paramError = resolveParamError(ref, connectionId);
    const paymentProviderName = resolvePaymentProviderDisplayName(searchParams.get('integration'))
    const rawRedirect = searchParams.get('redirect') ?? '/'
    const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/'

    const backendApi = new BackendApi(currentUser.getToken())

    const [isLoading, setIsLoading] = useState(!paramError);
    const [error, setError] = useState(paramError);
    const [syncJobsGroupId, setSyncJobsGroupId] = useState<string | null>(null);

    async function onLoad() {
        try {
            const response = await backendApi.Business__markConnectionComplete({
                ref: ref!,
                proxy_connection_id: connectionId!
            })

            setSyncJobsGroupId(response.sync_jobs_group_id ?? null)
            setIsLoading(false)
        } catch (error) {
            const reason = error.detail || error.toString();
            setError('Er is een fout opgetreden, error-code: "' + reason + '" neem contact met ons op voor verdere hulp. Excuses voor het ongemak')
        }
    }

    useEffect(() => {
        if (paramError) {
            return;
        }

        onLoad()
    }, []);

    return (
        <div>
            <Header/>
            <main className="bg-white py-24 sm:py-32">
                <LoadingFrame isLoading={false}>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8">

                        {!!error && <ErrorView error={error} onClick={() => window.location.replace('/my-profile')}/>}

                        {!error && isLoading && <div className="mx-auto max-w-2xl text-center"><LoadingView/></div>}

                        {!error && !isLoading && (paymentProviderName != null
                            ? <PaymentProviderConnectSuccessPage paymentProviderName={paymentProviderName}
                                                                 redirectTo={redirectTo}
                                                                 syncJobsGroupId={syncJobsGroupId}
                                                                 currentUser={currentUser}/>
                            : <AccountingConnectSuccessPage redirectTo={redirectTo}/>)}
                    </div>
                </LoadingFrame>
            </main>
        </div>
    )
}

